import { defineStore } from 'pinia';
import { getTaskViewApi, getDealTasksApi } from '@Api/dashboard.api';
import type { DashboardResponse, DealDashboard, TaskItem } from '@Types/dashboard.types';

export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        deals: [] as DealDashboard[],
        summary: null as DashboardResponse['summary'] | null,
        participantFirstName: null as string | null,
        isLoading: false,
        isLoaded: false,
        error: null as string | null,
        selectedDealId: null as string | null,
    }),
    getters: {
        activeDeal: (state): DealDashboard | null =>
            (state.selectedDealId
                ? state.deals.find((d) => d.dealId === state.selectedDealId)
                : undefined)
            ?? state.deals[0] ?? null,
        allPendingTasks: (state): TaskItem[] =>
            state.deals.flatMap((d) => d.pendingTasks),
        allInProgressTasks: (state): TaskItem[] =>
            state.deals.flatMap((d) => d.inProgressTasks),
        allCompletedTasks: (state): TaskItem[] =>
            state.deals.flatMap((d) => d.completedTasks),
        actionRequiredTasks: (state): TaskItem[] =>
            // Trust the BFF's authoritative ordering (Match pinned to position 0
            // when any task is locked). Sorting by sequenceOrder here would
            // override the pin and re-bury Match.
            state.deals.flatMap((d) => d.activeTasks),
        // Drawer-shown gate. Per Raghav's spec, `RcsConsentAt` is stamped on
        // any response (consent OR decline), so a non-null value means the
        // participant has answered and the drawer should not re-render.
        // Per-deal: switching to a deal with `rcsConsentAt = null` re-opens
        // the drawer for that deal.
        //
        // INTENTIONAL: `=== null` (strict) — not `== null`. Until BFF PR #38
        // lands, the field will be `undefined` at runtime (not present on the
        // response), and we want the drawer dormant in production until that
        // contract ships. Strict equality returns false for undefined; loose
        // would return true and the drawer would render unexpectedly. Do not
        // relax to `== null` without confirming the BFF contract is live.
        showWelcome(): boolean {
            return this.isLoaded
                && this.activeDeal !== null
                && this.activeDeal.rcsConsentAt === null;
        },
    },
    actions: {
        async loadDashboard() {
            this.isLoading = true;
            this.error = null;
            try {
                const data = await getTaskViewApi();
                this.deals = data.deals;
                this.summary = data.summary;
                this.participantFirstName = data.participantFirstName;
                this.isLoaded = true;
            } catch {
                this.error = 'Failed to load dashboard';
            } finally {
                this.isLoading = false;
            }
        },
        async selectDeal(dealId: string): Promise<void> {
            this.selectedDealId = dealId;
            try {
                const refreshed = await getDealTasksApi(dealId);
                const idx = this.deals.findIndex(d => d.dealId === dealId);
                if (idx !== -1) {
                    this.deals = [
                        ...this.deals.slice(0, idx),
                        { ...this.deals[idx], ...refreshed },
                        ...this.deals.slice(idx + 1),
                    ];
                }
            } catch {
                // Best-effort — keep the selection; BFF write may have fired anyway.
                // Don't blow away the user's selection on a network blip.
            }
        },
        // Local-only stamp for the harness + Phase 1 visual review. Phase 2
        // replaces the body with a call to useRcsConsent.captureConsent and
        // a follow-up loadDashboard so the BFF is the source of truth.
        dismissWelcomeLocally(messagingOptIn: boolean) {
            const deal = this.activeDeal;
            if (!deal) return;
            deal.rcsConsentAt = new Date().toISOString();
            deal.rcsConsent = messagingOptIn;
        },
    },
    persist: {
        pick: ['selectedDealId'],
        storage: sessionStorage,
    },
});
